const KEY = 'security-settings-v1';
let audit; try{ audit = require('../bread/auditLogService'); }catch{ audit = null; }

const defaultSettings = {
  passwordPolicy: {
    minLength: 12,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    preventCommonPasswords: true,
    preventPasswordReuse: 5,
    maxAge: 90, // days
    lockoutThreshold: 5,
    lockoutDuration: 30, // minutes
  },
  sessionSecurity: {
    sessionTimeout: 480, // minutes (8 hours)
    maxConcurrentSessions: 3,
    requireReauthForSensitive: true,
    logoutOnClose: false,
    rememberMe: true,
    rememberMeDuration: 30, // days
  },
  twoFactorAuth: {
    enabled: false,
    enforceForAdmins: true,
    allowedMethods: ['totp', 'sms', 'email'],
    backupCodes: true,
    gracePeriod: 7, // days for new users
  },
  apiSecurity: {
    keyRotationInterval: 90, // days
    maxKeysPerUser: 5,
    ipWhitelisting: false,
    rateLimit: 1000, // requests per hour
    requireHttps: true,
  },
  monitoring: {
    logFailedLogins: true,
    alertOnSuspiciousActivity: true,
    geoLocationTracking: true,
    deviceFingerprinting: false,
    emailNotifications: true,
    adminEmailAlerts: 'security@krotrust.org',
  },
  compliance: {
    gdprCompliant: true,
    dataRetention: 365, // days
    encryptionAtRest: true,
    encryptionInTransit: true,
    auditLogRetention: 2555, // days (7 years)
  }
};

function load(){
  try{ const raw = localStorage.getItem(KEY); return raw ? {...defaultSettings, ...JSON.parse(raw)} : defaultSettings; }catch{ return defaultSettings; }
}

let settings = load();
const subs = new Set();

function save(){ try{ localStorage.setItem(KEY, JSON.stringify(settings)); }catch{} }
function publish(){ save(); subs.forEach(cb=>{ try{ cb(settings); }catch{} }); }

export function subscribe(cb){ subs.add(cb); cb(settings); return ()=> subs.delete(cb); }
export function getSettings(){ return settings; }

export function updatePasswordPolicy(updates){
  settings.passwordPolicy = { ...settings.passwordPolicy, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.passwordPolicy.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function updateSessionSecurity(updates){
  settings.sessionSecurity = { ...settings.sessionSecurity, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.sessionSecurity.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function updateTwoFactorAuth(updates){
  settings.twoFactorAuth = { ...settings.twoFactorAuth, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.twoFactorAuth.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function updateApiSecurity(updates){
  settings.apiSecurity = { ...settings.apiSecurity, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.apiSecurity.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function updateMonitoring(updates){
  settings.monitoring = { ...settings.monitoring, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.monitoring.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function updateCompliance(updates){
  settings.compliance = { ...settings.compliance, ...updates };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.compliance.update', target: Object.keys(updates).join(','), status: 'success', ip: '-' }); }catch{}
}

export function resetToDefaults(){
  settings = { ...defaultSettings };
  publish();
  try{ audit && audit.add && audit.add({ user: 'system', action: 'security.resetToDefaults', target: 'all', status: 'success', ip: '-' }); }catch{}
}

export function validatePasswordPolicy(password){
  const p = settings.passwordPolicy;
  const errors = [];
  
  if(password.length < p.minLength) errors.push(`Password must be at least ${p.minLength} characters`);
  if(p.requireUppercase && !/[A-Z]/.test(password)) errors.push('Password must contain uppercase letters');
  if(p.requireLowercase && !/[a-z]/.test(password)) errors.push('Password must contain lowercase letters');
  if(p.requireNumbers && !/\d/.test(password)) errors.push('Password must contain numbers');
  if(p.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('Password must contain special characters');
  
  // Common password check (simplified)
  const common = ['password', '123456', 'qwerty', 'admin', 'welcome', 'login'];
  if(p.preventCommonPasswords && common.some(c=> password.toLowerCase().includes(c))) {
    errors.push('Password contains common words or patterns');
  }
  
  return { valid: errors.length === 0, errors };
}

export function getSecurityScore(){
  let score = 0;
  const p = settings.passwordPolicy;
  const s = settings.sessionSecurity;
  const t = settings.twoFactorAuth;
  const a = settings.apiSecurity;
  const m = settings.monitoring;
  const c = settings.compliance;
  
  // Password policy (25 points)
  if(p.minLength >= 12) score += 5;
  if(p.requireUppercase && p.requireLowercase && p.requireNumbers && p.requireSpecialChars) score += 10;
  if(p.lockoutThreshold <= 5) score += 5;
  if(p.maxAge <= 90) score += 5;
  
  // Session security (20 points)
  if(s.sessionTimeout <= 480) score += 5;
  if(s.maxConcurrentSessions <= 3) score += 5;
  if(s.requireReauthForSensitive) score += 5;
  if(!s.rememberMe || s.rememberMeDuration <= 30) score += 5;
  
  // Two-factor auth (25 points)
  if(t.enabled) score += 15;
  if(t.enforceForAdmins) score += 5;
  if(t.backupCodes) score += 5;
  
  // API security (15 points)
  if(a.keyRotationInterval <= 90) score += 5;
  if(a.requireHttps) score += 5;
  if(a.rateLimit <= 1000) score += 5;
  
  // Monitoring (10 points)
  if(m.logFailedLogins && m.alertOnSuspiciousActivity) score += 5;
  if(m.emailNotifications) score += 5;
  
  // Compliance (5 points)
  if(c.encryptionAtRest && c.encryptionInTransit) score += 5;
  
  return Math.min(100, score);
}

export function exportJSON(){ return settings; }
export function importJSON(obj){ if(!obj) return; settings = { ...defaultSettings, ...obj }; publish(); }